import { MotionProps, m as motion } from "framer-motion";
import { TCategory, TLink, isCategoryType } from "../../../../../types/link-types";
import { MutableRefObject, useEffect, useState } from "react";
import styles from "./navigation.module.less";
import Link from "../../../../elements/link/link";
import { useTranslation } from "react-i18next";
import { useAnimateFadeIn } from "../../../../../animation/animation";

type TNavigationProps = {
  isOpen?: boolean;
  scrollAllowedRef?: MutableRefObject<HTMLDivElement | null>;
  menuItems: Array<TCategory | TLink>;
  currentPageUrl?: string;
  closeMenuFunc: () => void;
  currentLanguage?: string;
  masterLanguage?: string;
};

const Navigation = ({
  isOpen,
  scrollAllowedRef,
  menuItems,
  currentPageUrl,
  closeMenuFunc,
  currentLanguage,
  masterLanguage,
}: TNavigationProps) => {
  const [visibleCategory, setVisibleCategory] = useState<TCategory | null>(null);
  const fadeInAnimation = useAnimateFadeIn({ easing: "easeInOut" });

  if (!Array.isArray(menuItems)) {
    return null;
  }

  const getCategoryFromUrl = (
    url: string | undefined,
    menuItems: Array<TCategory | TLink>,
    currentLanguage?: string,
    masterLanguage?: string
  ): TCategory | null => {
    const urlItems = url?.split("/");

    if (!urlItems) {
      return null;
    }

    for (const urlItem of urlItems) {
      if (!urlItem) {
        continue;
      }
      for (const menuItem of menuItems) {
        if (isCategoryType(menuItem)) {
          if (currentLanguage === masterLanguage) {
            if (menuItem.mainCategoryLink?.url === `/${urlItem}`) {
              return menuItem;
            }
          } else {
            if (menuItem.mainCategoryLink?.url === `/${currentLanguage}/${urlItem}`) {
              return menuItem;
            }
          }
        }
      }
    }

    return null;
  };

  useEffect(() => {
    if (isOpen) {
      const activeCategory = getCategoryFromUrl(currentPageUrl as string, menuItems, currentLanguage, masterLanguage);
      setVisibleCategory(activeCategory);
    }
  }, [currentPageUrl, isOpen, currentLanguage, masterLanguage]);

  const levelOneItems = menuItems.map((item) => {
    if (isCategoryType(item)) {
      return (
        <li className={`${styles.levelOneListItem} ${styles.listItem} ${styles.large}`} key={item.title}>
          <button className={`${styles.openCategoryButton} body-l bold`} onClick={() => setVisibleCategory(item)}>
            <span className={styles.openCategoryButtonTitle}>{item.title}</span>
            <span className={`${styles.iconSize} ui-icon-chevron-right`}></span>
          </button>
        </li>
      );
    }

    return (
      <li className={`${styles.levelOneListItem} ${styles.listItem} ${styles.large}`} key={item.title}>
        <Link
          {...item}
          currentPageUrl={currentPageUrl}
          onClickCurrentPageLink={closeMenuFunc}
          className={`${styles.link} body-l bold`}
        />
      </li>
    );
  });

  return (
    <div ref={scrollAllowedRef} className={styles.wrapper}>
      {visibleCategory === null && (
        <motion.ul {...fadeInAnimation} key="level-one-items" className={styles.levelOneList}>
          {levelOneItems}
        </motion.ul>
      )}
      {visibleCategory && (
        <Category
          key={"visible-category"}
          closeMenuFunc={closeMenuFunc}
          currentPageUrl={currentPageUrl}
          category={visibleCategory}
          goBackFunc={() => setVisibleCategory(null)}
          animation={fadeInAnimation}
        />
      )}
    </div>
  );
};

type TCategoryProps = {
  category: TCategory;
  goBackFunc: CallableFunction;
  currentPageUrl?: string;
  closeMenuFunc: () => void;
  animation: MotionProps | undefined;
};

const Category = ({
  category,
  currentPageUrl: currentPageUrl,
  goBackFunc,
  closeMenuFunc,
  animation,
}: TCategoryProps) => {
  const { t } = useTranslation();

  return (
    <motion.div {...animation}>
      <button className={`${styles.backButton} ${styles.listItem}`} onClick={() => goBackFunc()}>
        <span className={`${styles.iconSize} ui-icon-chevron-left`}></span>
        <span className={"body-s"}>{t("BACK")}</span>
      </button>
      <ul className={styles.categoryLinkList}>
        <li className={`${styles.listItem} ${styles.large} body-l bold`} key={"category-" + category.title}>
          {category?.mainCategoryLink ? (
            <Link
              {...category.mainCategoryLink}
              currentPageUrl={currentPageUrl}
              onClickCurrentPageLink={closeMenuFunc}
              className={[styles.link, styles.mainCategoryWrapper].join(" ")}
            >
              <span className={styles.mainCategoryTitle}>{category.title}</span>
              <span className={[styles.mainCategoryIcon, "ui-icon-arrow-right"].join(" ")} />
            </Link>
          ) : (
            <>{category.title}</>
          )}
        </li>
        {category.links?.map((link) => {
          return (
            <li className={`${styles.listItem} body-base`} key={link.title}>
              <Link
                {...link}
                currentPageUrl={currentPageUrl}
                onClickCurrentPageLink={closeMenuFunc}
                className={[styles.link, styles.secondaryItem].join(" ")}
              />
            </li>
          );
        })}
      </ul>
    </motion.div>
  );
};

export default Navigation;
